import { useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import type { Database, Json } from '@/types/database.generated';

// ── Types ─────────────────────────────────────────────────────────────────────

export type ToothStatusRow = Database['public']['Tables']['patient_tooth_status']['Row'];
export type ToothTreatmentRow = Database['public']['Tables']['tooth_treatments']['Row'];
export type TreatmentPlanRow = Database['public']['Tables']['tooth_treatment_plans']['Row'];
export type DentalShortcutRow = Database['public']['Tables']['dental_shortcuts']['Row'];
export type DentalProcedureCodeRow = Database['public']['Tables']['dental_procedure_codes']['Row'];
export type ToothImageRow = Database['public']['Tables']['tooth_images']['Row'];
export type PerioMeasurementRow = Database['public']['Tables']['perio_measurements']['Row'];

export type ToothStatusType =
  | 'healthy' | 'caries' | 'filled' | 'crown' | 'missing'
  | 'implant' | 'root_canal' | 'bridge' | 'extraction_planned'
  | 'fracture' | 'periapical_abscess' | 'veneer' | 'post_and_core';

export interface ToothStatus {
  id: string;
  tooth_fdi: string;
  tooth_type: 'permanent' | 'primary' | 'implant' | 'missing' | 'unerupted';
  status: ToothStatusType[];
  surfaces: Record<string, { status?: string; material?: string }>;
  notes?: string;
  last_examined_at?: string;
  last_examined_by?: string;
}

export interface ToothTreatment {
  id: string;
  tooth_fdi: string;
  treatment_type: string;
  diagnosis?: string;
  treatment_description: string;
  surfaces_treated?: string[];
  material_used?: string;
  anesthesia?: { type?: string; method?: string; amount?: string };
  medications?: Array<{
    name: string;
    strength?: string;
    dosage?: string;
    frequency?: string;
    duration?: string;
  }>;
  consent_given: boolean;
  consent_method?: 'verbal' | 'written' | 'electronic';
  consent_notes?: string;
  estimated_cost?: number;
  actual_cost?: number;
  treatment_date: string;
  performed_by: string;
  performer_name?: string;
}

// ── Query Keys ────────────────────────────────────────────────────────────────

export const toothChartQueryKey = (patientId: string | null) =>
  ['dental', 'tooth-chart', patientId] as const;

export const toothStatusQueryKey = (patientId: string | null, toothFdi: string | null) =>
  ['dental', 'tooth-status', patientId, toothFdi] as const;

export const treatmentHistoryQueryKey = (patientId: string | null, toothFdi?: string | null) =>
  ['dental', 'treatments', patientId, toothFdi ?? null] as const;

// ── Queries ───────────────────────────────────────────────────────────────────

/**
 * Fetch the complete tooth chart for a patient (all teeth status).
 * Uses the `get_patient_tooth_chart` RPC which returns all tooth status records.
 */
export function usePatientToothChart(patientId: string | null) {
  return useQuery({
    queryKey: toothChartQueryKey(patientId),
    enabled: Boolean(patientId),
    queryFn: async (): Promise<ToothStatus[]> => {
      if (!patientId) return [];
      const { data, error } = await supabase.rpc('get_patient_tooth_chart', {
        p_patient_id: patientId,
      });
      if (error) throw error;
      return (data ?? []) as unknown as ToothStatus[];
    },
    staleTime: 30_000,
  });
}

/**
 * Fetch status for a specific tooth.
 * Queries the tooth chart and filters for the specific tooth.
 */
export function useToothStatus(patientId: string | null, toothFdi: string | null) {
  const toothChartQuery = usePatientToothChart(patientId);

  return useQuery({
    queryKey: toothStatusQueryKey(patientId, toothFdi),
    enabled: Boolean(patientId && toothFdi),
    queryFn: async (): Promise<ToothStatus | null> => {
      if (!patientId || !toothFdi) return null;
      
      // Use data from tooth chart if available
      if (toothChartQuery.data) {
        const tooth = toothChartQuery.data.find((t) => t.tooth_fdi === toothFdi);
        return tooth ?? null;
      }

      // Fallback: query directly
      const { data, error } = await supabase
        .from('patient_tooth_status')
        .select('*')
        .eq('patient_id', patientId)
        .eq('tooth_fdi', toothFdi)
        .maybeSingle();
      if (error) throw error;
      return data as ToothStatus | null;
    },
    staleTime: 30_000,
  });
}

/**
 * Fetch treatment history for a patient, optionally filtered to a specific tooth.
 * Uses the `get_tooth_treatment_history` RPC.
 */
export function useToothTreatmentHistory(
  patientId: string | null,
  toothFdi?: string | null,
) {
  return useQuery({
    queryKey: treatmentHistoryQueryKey(patientId, toothFdi),
    enabled: Boolean(patientId),
    queryFn: async (): Promise<ToothTreatment[]> => {
      if (!patientId) return [];
      const { data, error } = await supabase.rpc('get_tooth_treatment_history', {
        p_patient_id: patientId,
        p_tooth_fdi: toothFdi ?? undefined,
      });
      if (error) throw error;
      return (data ?? []) as unknown as ToothTreatment[];
    },
    staleTime: 10_000,
  });
}

// ── Mutations ─────────────────────────────────────────────────────────────────

/**
 * Upsert tooth status (create or update) — v2 pathway preferred.
 * Tries `upsert_tooth_status_v2` (TAND-1) first; falls back to
 * `upsert_tooth_status` (TEXT[] backward-compat) if v2 isn't reachable.
 * Both accept statuses TEXT[] and root_zones JSONB as of TAND-1.
 */
export function useUpsertToothStatus(_clinicId: string | null | undefined) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: {
      patientId: string;
      toothFdi: string;
      toothType?: 'permanent' | 'primary' | 'implant' | 'missing' | 'unerupted';
      status: ToothStatusType[];
      statuses?: ToothStatusType[]; // preferred: explicit multi-status (TAND-1)
      surfaces?: Record<string, unknown>;
      rootZones?: Json; // TAND-1: pass root_zones as first-class JSONB
      notes?: string;
    }) => {
      // Resolve the statuses array — prefer explicit `statuses`, fall back to `status`
      const resolvedStatuses = params.statuses ?? params.status;

      // Attempt v2 RPC first (TAND-1 schema)
      const { data, error } = await supabase.rpc('upsert_tooth_status_v2', {
        p_patient_id: params.patientId,
        p_tooth_fdi: params.toothFdi,
        p_tooth_type: params.toothType ?? 'permanent',
        p_statuses: resolvedStatuses as unknown as string[],
        p_surfaces: (params.surfaces ?? {}) as Json,
        p_root_zones: (params.rootZones ?? []) as Json,
        p_notes: params.notes || undefined,
      });

      if (error) {
        // Fallback to backward-compat upsert_tooth_status (TEXT[] version)
        const { data: fallbackData, error: fallbackError } = await supabase.rpc('upsert_tooth_status', {
          p_patient_id: params.patientId,
          p_tooth_fdi: params.toothFdi,
          p_tooth_type: params.toothType ?? 'permanent',
          p_status: resolvedStatuses as unknown as string[],
          p_surfaces: (params.surfaces ?? {}) as Json,
          p_notes: params.notes || undefined,
        });
        if (fallbackError) throw fallbackError;
        return fallbackData;
      }

      return data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: toothChartQueryKey(variables.patientId),
      });
      queryClient.invalidateQueries({
        queryKey: toothStatusQueryKey(variables.patientId, variables.toothFdi),
      });
    },
  });
}

/**
 * Record a tooth treatment.
 * Uses the `record_tooth_treatment` RPC to create a treatment record and
 * optionally update the tooth status.
 */
export function useRecordTreatment(clinicId: string | null | undefined) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: {
      patientId: string;
      toothFdi: string;
      treatmentType: string;
      diagnosis?: string;
      treatmentDescription: string;
      surfacesTreated?: string[];
      materialUsed?: string;
      anesthesia?: { type?: string; method?: string; amount?: string };
      medications?: Array<{
        name: string;
        strength?: string;
        dosage?: string;
        frequency?: string;
        duration?: string;
      }>;
      consentGiven: boolean;
      consentMethod?: 'verbal' | 'written' | 'electronic';
      consentNotes?: string;
      estimatedCost?: number;
      actualCost?: number;
      treatmentDate?: string;
      journalEntryId?: string;
      updateToothStatus?: 'healthy' | 'caries' | 'filled' | 'crown' | 'missing' | 'implant' | 'root_canal' | 'bridge' | 'extraction_planned';
    }) => {
      if (!clinicId) throw new Error('No clinic ID provided');
      const { data, error } = await supabase.rpc('record_tooth_treatment', {
        p_clinic_id: clinicId,
        p_patient_id: params.patientId,
        p_tooth_fdi: params.toothFdi,
        p_treatment_type: params.treatmentType,
        p_diagnosis: params.diagnosis ?? undefined,
        p_treatment_description: params.treatmentDescription,
        p_surfaces_treated: params.surfacesTreated ?? undefined,
        p_material_used: params.materialUsed ?? undefined,
        p_anesthesia: (params.anesthesia ?? undefined) as Json | undefined,
        p_medications: (params.medications ?? undefined) as Json | undefined,
        p_consent_given: params.consentGiven,
        p_consent_method: params.consentMethod ?? undefined,
        p_consent_notes: params.consentNotes ?? undefined,
        p_estimated_cost: params.estimatedCost ?? undefined,
        p_actual_cost: params.actualCost ?? undefined,
        p_treatment_date: params.treatmentDate ?? undefined,
        p_journal_entry_id: params.journalEntryId ?? undefined,
        p_update_tooth_status: params.updateToothStatus ?? undefined,
      });
      if (error) throw error;
      return data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: toothChartQueryKey(variables.patientId),
      });
      queryClient.invalidateQueries({
        queryKey: treatmentHistoryQueryKey(variables.patientId),
      });
      queryClient.invalidateQueries({
        queryKey: treatmentHistoryQueryKey(variables.patientId, variables.toothFdi),
      });
      queryClient.invalidateQueries({
        queryKey: toothStatusQueryKey(variables.patientId, variables.toothFdi),
      });
    },
  });
}

// ── Realtime ──────────────────────────────────────────────────────────────────

/**
 * Subscribe to realtime updates for dental data (tooth status, treatments, and
 * treatment plans). Automatically invalidates queries when changes occur.
 * Call once per component mount; cleans up on unmount.
 */
export function useDentalRealtime(patientId: string | null) {
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!patientId) return;

    // Unique-per-mount channel name. Two reasons:
    //   1. Supabase JS caches channel instances by name; if two components both
    //      call `supabase.channel('dental:patient-X')`, the second receives the
    //      already-subscribed instance and any `.on()` call after that throws
    //      "cannot add `postgres_changes` callbacks ... after `subscribe()`".
    //   2. React 18 StrictMode mounts effects twice in dev; the second mount
    //      would hit the same race even within one component.
    // Even though the hook is now hoisted to DentalModulePage (only one caller
    // per module mount), we keep the per-mount suffix as belt-and-suspenders.
    const suffix =
      typeof crypto !== 'undefined' && 'randomUUID' in crypto
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2)}`;

    const channel = supabase
      .channel(`dental:patient-${patientId}:${suffix}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'patient_tooth_status',
          filter: `patient_id=eq.${patientId}`,
        },
        () => {
          queryClient.invalidateQueries({
            queryKey: toothChartQueryKey(patientId),
          });
        },
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'tooth_treatments',
          filter: `patient_id=eq.${patientId}`,
        },
        () => {
          queryClient.invalidateQueries({
            queryKey: treatmentHistoryQueryKey(patientId),
          });
          queryClient.invalidateQueries({
            queryKey: ['dental', 'treatments-filtered', patientId],
          });
          queryClient.invalidateQueries({
            queryKey: ['dental', 'session-today', patientId],
          });
        },
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'tooth_treatment_plans',
          filter: `patient_id=eq.${patientId}`,
        },
        () => {
          queryClient.invalidateQueries({
            queryKey: ['dental', 'treatment-plans', patientId],
          });
          queryClient.invalidateQueries({
            queryKey: ['dental', 'session-today', patientId],
          });
        },
      )
      .subscribe();

    return () => {
      void supabase.removeChannel(channel);
    };
  }, [patientId, queryClient]);
}

// ── Filtered history ──────────────────────────────────────────────────────────

export interface TreatmentHistoryFilters {
  dateFrom?: string | null;
  dateTo?: string | null;
  toothFdi?: string | null;
  performerId?: string | null;
  treatmentType?: string | null;
  onlyOtherPractice?: boolean;
  onlyOwnPractice?: boolean;
  freeText?: string | null;
}

export const filteredHistoryQueryKey = (
  patientId: string | null,
  filters: TreatmentHistoryFilters,
) => ['dental', 'treatments-filtered', patientId, filters] as const;

/**
 * Fetch treatment history with rich filter support.
 * Queries `tooth_treatments` directly (RLS-protected SELECT).
 */
export function usePatientTreatmentHistoryFiltered(
  patientId: string | null,
  filters: TreatmentHistoryFilters = {},
) {
  return useQuery({
    queryKey: filteredHistoryQueryKey(patientId, filters),
    enabled: Boolean(patientId),
    queryFn: async () => {
      if (!patientId) return [];

      let query = supabase
        .from('tooth_treatments')
        .select('*')
        .eq('patient_id', patientId)
        .order('treatment_date', { ascending: false })
        .order('created_at', { ascending: false });

      if (filters.dateFrom) {
        query = query.gte('treatment_date', filters.dateFrom);
      }
      if (filters.dateTo) {
        query = query.lte('treatment_date', filters.dateTo);
      }
      if (filters.toothFdi) {
        query = query.eq('tooth_fdi', filters.toothFdi);
      }
      if (filters.performerId) {
        query = query.eq('performed_by', filters.performerId);
      }
      if (filters.treatmentType) {
        query = query.eq('treatment_type', filters.treatmentType);
      }
      if (filters.onlyOtherPractice) {
        query = query.eq('performed_at_other_practice', true);
      }
      if (filters.onlyOwnPractice) {
        query = query.eq('performed_at_other_practice', false);
      }
      if (filters.freeText) {
        query = query.or(
          `treatment_description.ilike.%${filters.freeText}%,diagnosis.ilike.%${filters.freeText}%`,
        );
      }

      const { data, error } = await query;
      if (error) throw error;
      return data ?? [];
    },
    staleTime: 15_000,
  });
}

// Helper to satisfy unused-export linter on Json
export type { Json };
